/**
=========================================================
* Material Dashboard 2 PRO React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";

// Material Dashboard 2 PRO React base styles
import breakpoints from "assets/theme/base/breakpoints";

// Images
import burceMars from "assets/images/bruce-mars.jpg";
import backgroundImage from "assets/images/bg-profile.jpeg";

import fallbackBackgroundImage from "assets/images/bg-profile.jpeg";
import fallbackAvatarImage from "assets/images/bruce-mars.jpg";

import API_URL from "../../../../../config";

function Header({ children, eventName, image, avatarImage }) {
  const [tabsOrientation, setTabsOrientation] = useState("horizontal");
  const [tabValue, setTabValue] = useState(0);

  const [dynamicImageUrl, setDynamicImageUrl] = useState(fallbackBackgroundImage);
  const [dynamicAvatarUrl, setDynamicAvatarUrl] = useState(fallbackAvatarImage);

  const buildImageUrl = (imagePath, isAvatar = false) => {
    if (!imagePath) {
      console.log(
        `No image path provided for ${isAvatar ? "avatar" : "background"}, using fallback.`
      );
      return isAvatar ? fallbackAvatarImage : fallbackBackgroundImage;
    }

    const formattedPath = imagePath.replace(/\\/g, "/");

    const imageUrl = `${formattedPath}`;

    console.log(`Constructed ${isAvatar ? "avatar" : "background"} image URL:`, imageUrl);
    return imageUrl;
  };

  useEffect(() => {
    if (!API_URL) {
      console.error("API_URL is not defined. Cannot build image URLs.");
      setDynamicImageUrl(fallbackBackgroundImage);
      setDynamicAvatarUrl(fallbackAvatarImage);
    } else {
      setDynamicImageUrl(buildImageUrl(image, false));

      setDynamicAvatarUrl(buildImageUrl(avatarImage, true));
    }

    function handleTabsOrientation() {
      return window.innerWidth < breakpoints.values.sm
        ? setTabsOrientation("vertical")
        : setTabsOrientation("horizontal");
    }

    window.addEventListener("resize", handleTabsOrientation);
    handleTabsOrientation();
    return () => window.removeEventListener("resize", handleTabsOrientation);
  }, [tabsOrientation, image, avatarImage]);
  const handleSetTabValue = (event, newValue) => setTabValue(newValue);

  return (
    <MDBox position="relative" mb={3}>
      <MDBox
        display="flex"
        alignItems="center"
        position="relative"
        minHeight="18.75rem"
        borderRadius="xl"
        sx={{
          backgroundImage: ({ functions: { rgba, linearGradient }, palette: { gradients } }) =>
            `${linearGradient(
              rgba(gradients.info.main, 0.6),
              rgba(gradients.info.state, 0.6)
            )}, url(${dynamicImageUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "0% 15%",
          overflow: "hidden",
        }}
      />

      <Card
        sx={{
          position: "relative",
          mt: -8,
          mx: 3,
          py: 1,
          px: 2,
        }}
      >
        <Grid container spacing={1} alignItems="center">
          <Grid item>
            <MDAvatar
              src={dynamicAvatarUrl}
              alt="profile-image"
              size="xl"
              shadow="sm"
              sx={{
                "& img": {
                  objectFit: "cover",
                  width: "100%",
                  height: "100%",
                },
              }}
            />
          </Grid>

          <Grid item>
            <MDBox height="100%" mt={0} lineHeight={1}>
              <MDTypography variant="h5" fontWeight="medium">
                {eventName}
              </MDTypography>
            </MDBox>
          </Grid>
        </Grid>

        {children}
      </Card>
    </MDBox>
  );
}

Header.defaultProps = {
  children: "",
  image: null,
  avatarImage: null,
  eventName: "",
};

Header.propTypes = {
  children: PropTypes.node,
  image: PropTypes.string,
  avatarImage: PropTypes.string,
  eventName: PropTypes.string.isRequired,
};

export default Header;
